import * as Sentry from "@sentry/vue";
import {
  createRouter,
  createWebHistory,
  type RouteRecordName,
} from "vue-router";
import { AppIdentifier } from "@/shared/@types/AppIdentifier";
import { AppRouteNames } from "@/shared/@types/AppRouteNames";
import { WebStoreId } from "@/shared/@types/WebStoreId";
import { useOciSession } from "@/shared/auth/useOciSession.composable";
import { isAuthenticated } from "@/shared/auth/isAuthenticated.helper";
import { isFrontendAdmin } from "@/shared/auth/isFrontendAdmin.helper";
import { getAppByPath } from "@/shared/helper/app.helper";
import { usePermission } from "@/shared/composables/usePermission.composable";
import { useNotifications } from "@/shared/composables/useNotifications.composable";
import { i18n } from "@/shared/i18n/i18n";
import { useAuthenticatedUsersOnly } from "@/shared/router/useAuthenticatedUsersOnly.composable";
import { useAppStateStore } from "@/shared/stores/AppStateStore";
import { routes } from "./routes";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0, left: 0 };
  },
});

router.beforeEach(async (to, from) => {
  // To ensure that there is no flicker of the loading overlay when navigating between routes,
  // we wait for the route change to complete before hiding the loading overlay.
  const { activateWaitForRouteChange } = useAppStateStore();
  activateWaitForRouteChange();

  // --- OCI initialization -------------------------------------------------------------------
  const {
    isOciSession,
    shouldInitializeOciSession,
    initializeOciSession,
    clearOciSession,
  } = useOciSession();

  if (shouldInitializeOciSession(to.hash)) {
    const isOciSessionInitialized = initializeOciSession(to.hash);

    if (isOciSessionInitialized) {
      // If the session has been initialized successfully, redirect to the same path without the hash parameters to avoid re-initialization on page reload
      return {
        path: to.path,
        query: to.query,
        hash: "",
      };
    }
  }

  const userIsAuthenticated = await isAuthenticated();
  const appToVisit = getAppByPath(to.path);

  // ----- Routes with all users allowed ------------------------------------------------------
  const unrestrictedRoutes: RouteRecordName[] = [
    AppRouteNames.GeneralError,
    AppRouteNames.Maintenance,
    AppRouteNames.Impersonate,
    AppRouteNames.RegistrationOverview,
    AppRouteNames.MobilityUserRegistration, // The mobility registration route (for an existing user) is accessible even if the user is logged in
    AppRouteNames.MeterReport, // The permissions are checked inside FBS app route's `beforeEnter`
    AppRouteNames.FbsHubMeterReport, // The permissions are checked inside FBS app route's `beforeEnter`
  ];

  if (unrestrictedRoutes.includes(to.name)) return true;

  // ----- Routes with unauthenticated users only -----------------------------------------------
  const unauthenticatedOnlyRoutes: RouteRecordName[] = [
    AppRouteNames.ThgQuoteUnauthenticatedVehicleRegistration,
    AppRouteNames.Login,
    AppRouteNames.DevLogin,
  ];

  const isUnauthenticatedOnlyRoute =
    appToVisit === AppIdentifier.Registration ||
    unauthenticatedOnlyRoutes.includes(to.name);

  if (!userIsAuthenticated) {
    // If the user is not authenticated, all routes of the login and registration apps are accessible (this includes /thg-quote/registration)
    if (isUnauthenticatedOnlyRoute) return true;

    // If the user is not authenticated and tries to access a route that requires authentication redirect to login
    return { name: AppRouteNames.Login };
  }

  // ----- App missing or not found -----------------------------------------------------------
  if (!appToVisit) return { name: AppRouteNames.Dashboard };

  // ----- Routes with authenticated users only -----------------------------------------------
  const {
    hasUserData,
    hasFetchingUserError,
    initializeUserData,
    accessRouteWithCustomerRequired,
    needsToCompleteRegistration,
    hasOtherCustomerWithPermission,
    selectCustomerWithPermission,
    hasPermissionForWebStore,
    canAccessRouteWithoutAppPermission,
    canApplyForAccountManagerRole,
  } = useAuthenticatedUsersOnly(to);

  if (!hasUserData.value) await initializeUserData(appToVisit);
  if (hasFetchingUserError.value) return { name: AppRouteNames.GeneralError };

  // ----- Admin control center: only accessible for frontend admins --------------------------
  if (appToVisit === AppIdentifier.AdminControlCenter) {
    return (await isFrontendAdmin()) || { name: AppRouteNames.Dashboard };
  }

  const { hasPermissionForApp } = usePermission();

  if (
    accessRouteWithCustomerRequired.value &&
    needsToCompleteRegistration.value &&
    typeof to.name === "string"
  ) {
    return {
      name: AppRouteNames.FinishRegistration,
      query: { returnTo: to.fullPath },
    };
  }

  if (
    to.name === AppRouteNames.FinishRegistration &&
    !needsToCompleteRegistration.value
  ) {
    return { name: AppRouteNames.Dashboard };
  }

  // OCI users can only access Gas Shop routes
  if (isOciSession.value) {
    if (
      appToVisit === AppIdentifier.GasShop &&
      hasPermissionForWebStore(to.params.webStoreId as string)
    ) {
      return true;
    } else if (hasPermissionForWebStore(WebStoreId.DE)) {
      // OCI is currently only supported in the German shop
      return {
        name: AppRouteNames.GasShopStart,
        params: { webStoreId: WebStoreId.DE },
      };
    } else {
      Sentry.captureException(
        `OCI user does not have the required web store permission (VKORG) for ${to.params.webStoreId}.`,
      );
      clearOciSession();
      return {
        name: AppRouteNames.Login,
      };
    }
  }

  if (!hasPermissionForApp(appToVisit)) {
    if (canAccessRouteWithoutAppPermission.value) return true;

    // The WID account management is accessible to every customer that can apply for the manager role as well
    if (
      appToVisit === AppIdentifier.WestfalenIdAccountManagement &&
      canApplyForAccountManagerRole.value
    )
      return true;

    // If the user comes from the wid-services and the selected customer has no access to the app,
    // try to select a customer with the required permission
    if (
      from.name === AppRouteNames.WestfalenIdAccountManagementServices &&
      (await selectCustomerWithPermission(appToVisit))
    )
      return true;

    // If the user has another customer with permission, do not switch the selected customer automatically
    // Instead, show a notification informing the user that they do not have access with the selected customer
    if (hasOtherCustomerWithPermission(appToVisit)) {
      const { addInfoSnackbar } = useNotifications();
      addInfoSnackbar(
        i18n.t("shared.notifications.noAppAccessForSelectedCustomer"),
        6000,
      );
    }

    return { name: AppRouteNames.Dashboard };
  }

  const isMissingWebStoreId =
    !to.params.webStoreId && to.name !== AppRouteNames.GasShopAddCustomer;
  const isMissingPermissionForWebStore =
    to.params.webStoreId &&
    !hasPermissionForWebStore(to.params.webStoreId as string);

  if (
    appToVisit === AppIdentifier.GasShop &&
    (isMissingWebStoreId || isMissingPermissionForWebStore)
  ) {
    return { name: AppRouteNames.Dashboard };
  }

  return true;
});

router.afterEach(() => {
  // Manually scroll to the top of the main content area after each route change,
  // as the default scroll behavior does not work with the sidebar layout
  document.querySelector(".sidebar-layout--main")?.scrollTo(0, 0);

  // Hide loading overlay after route changes, as hiding it directly after a service call
  // in the composables might trigger a flicker because of redirects (e.g. logout)
  const appStateStore = useAppStateStore();
  appStateStore.hideLoadingOverlay();
});

export default router;
